import { SegmentSelect } from '@/design-system/components/buttons/segmentSelect/SegmentSelect';
import { DatePicker } from '@/design-system/components/datePicker/DatePicker';
import { Dropdown } from '@/design-system/components/dropdown';
import { Icon } from '@/design-system/components/icons/Icon';
import { TextBody, TextCaption, TextSmall } from '@/design-system/components/typography';
import { IntervalBillPayment, ScheduleBillPayment } from '@/features/bill-payment/enum';
import { UseBillPaymentFormProp } from '@/features/bill-payment/hooks/useBillPaymentForm.hook';
import { SxProp, View } from 'dripsy';
import moment from 'moment-timezone';
import React from 'react';
import { useTranslation } from 'react-i18next';
interface ScheduleComponentWebProps {
  sx?: SxProp;
  billPaymentProp: UseBillPaymentFormProp;
}

export const ScheduleComponentWebComponent: React.FC<ScheduleComponentWebProps> = ({ sx, billPaymentProp }) => {
  const { t } = useTranslation('global-bill-payment');
  const {
    container,
    leftContainer,
    rightContainer,
    number,
    textNumber,
    textScheduleDate,
    textDateOfFirstPayment,
    dropdownFrequencyWrapper,
    dropdown,
    viewTitleIconHorizontal,
    viewIcon,
    textError,
    viewRemarksMargin,
    viewTabWrapper,
    textDescription
  } = styles;

  const {
    scheduleForm: {
      scheduleTab,
      schedule,
      dateOfFirstPayment,
      interval,
      paymentEndDate,
      setSchedule,
      setDateOfFirstPayment,
      setScheduleTab,
      setInterval,
      setPaymentEndDate
    },
    general: { billPaymentState }
  } = billPaymentProp;

  const isErrorScheduleDate = billPaymentState && billPaymentState.scheduleDate && billPaymentState.scheduleDate.error;
  const isErrorDateOfFirstPayment = billPaymentState && billPaymentState.dateOfFirstPayment && billPaymentState.dateOfFirstPayment.error;
  const isErrorInterval = billPaymentState && billPaymentState.interval && billPaymentState.interval.error;
  const isErrorPaymentEndDate = billPaymentState && billPaymentState.paymentEndDate && billPaymentState.paymentEndDate.error;
  return (
    <View sx={{ ...container, ...sx }}>
      <View sx={leftContainer}>
        <View sx={number}>
          <TextBody sx={textNumber}>3</TextBody>
        </View>
        <TextCaption semiBold={true}>{t('global-bill-payment::payment-details.title--schedule-your-payment')}</TextCaption>
        <TextCaption sx={textDescription}>{t('global-bill-payment::payment-details.text--toggle-options')}</TextCaption>
      </View>
      <View sx={{ flex: 4 }} />
      <View sx={rightContainer}>
        <View sx={viewTabWrapper}>
          <SegmentSelect
            key="schedule-header-suffix-0"
            data={[
              {
                labelKey: t('global-bill-payment::glossary.now'),
                value: ScheduleBillPayment.NOW
              },
              {
                labelKey: t('global-bill-payment::glossary.later'),
                value: ScheduleBillPayment.FUTURE
              },
              {
                labelKey: t('global-bill-payment::glossary.recurring'),
                value: ScheduleBillPayment.RECURRING
              }
            ]}
            onValueChange={(value) => {
              setScheduleTab(value);
            }}
            buttonWidth={104}
          />
        </View>
        {/* Now */}
        {scheduleTab === ScheduleBillPayment.NOW && (
          <View>
            <View sx={{ ...styles.viewRow, ...{ mt: '$5' } }}>
              <TextSmall sx={styles.textNumberSchedule}>1.</TextSmall>
              <View sx={{ ...styles.viewColumn, ...{ flex: 1 } }}>
                <TextSmall sx={{ ...styles.textNote, ...styles.textNoteMarginSchedule }}>
                  {t('global-bill-payment::instruction.text--notice-payment-instruction-will')}
                </TextSmall>
              </View>
            </View>
            <View sx={styles.viewRow}>
              <TextSmall sx={styles.textNumberSchedule}>2.</TextSmall>
              <View sx={{ ...styles.viewColumn, ...{ flex: 1 } }}>
                <TextSmall sx={{ ...styles.textNote, ...styles.textNoteMarginSchedule }}>
                  {t('global-bill-payment::instruction.text--notice-should-the-instruction-rejected')}
                </TextSmall>
              </View>
            </View>
            <View sx={viewRemarksMargin} />
          </View>
        )}
        {/* Later */}
        {scheduleTab === ScheduleBillPayment.FUTURE && (
          <>
            <TextCaption semiBold sx={textScheduleDate}>
              {t('global-bill-payment::glossary.scheduled-date')}
            </TextCaption>
            <DatePicker
              dateProps={schedule}
              onValueChange={setSchedule}
              sx={{ borderColor: isErrorScheduleDate ? '$danger' : '$borderButton' }}
              options={{
                minDate: moment().add('1', 'days')
              }}
            />
            {isErrorScheduleDate && billPaymentState.scheduleDate && (
              <TextCaption danger sx={textError}>
                {billPaymentState.scheduleDate.message}
              </TextCaption>
            )}

            <View sx={{ ...styles.viewRow, ...{ mt: '$5' } }}>
              <TextSmall sx={styles.textNumberSchedule}>1.</TextSmall>
              <View sx={{ ...styles.viewColumn, ...{ flex: 1 } }}>
                <TextSmall sx={{ ...styles.textNote, ...styles.textNoteMarginSchedule }}>
                  {t('global-bill-payment::instruction.text--notice-should-the-instruction-rejected')}
                </TextSmall>
              </View>
            </View>
            <View sx={styles.viewRow}>
              <TextSmall sx={styles.textNumberSchedule}>2.</TextSmall>
              <View sx={{ ...styles.viewColumn, ...{ flex: 1 } }}>
                <TextSmall sx={{ ...styles.textNote, ...styles.textNoteMarginSchedule }}>
                  {t('global-bill-payment::instruction.text--notice-please-ensure')}
                </TextSmall>
              </View>
            </View>
            <View sx={viewRemarksMargin} />
          </>
        )}
        {/* Recurring */}
        {scheduleTab === ScheduleBillPayment.RECURRING && (
          <>
            <View sx={viewTitleIconHorizontal}>
              <TextCaption semiBold sx={textDateOfFirstPayment}>
                {t('global-bill-payment::glossary.date-of-first-payment')}
              </TextCaption>
              <View sx={viewIcon}>
                <Icon name="info" color="#000000" width={15} height={15} />
              </View>
            </View>
            <DatePicker
              sx={{ borderColor: isErrorDateOfFirstPayment ? '$danger' : '$borderButton' }}
              dateProps={dateOfFirstPayment}
              onValueChange={(value) => {
                setDateOfFirstPayment(value);
                setPaymentEndDate(null);
              }}
              options={{
                minDate: moment()
              }}
            />
            {isErrorDateOfFirstPayment && (
              <TextCaption danger sx={textError}>
                {billPaymentState.dateOfFirstPayment?.message}
              </TextCaption>
            )}
            <View sx={viewTitleIconHorizontal}>
              <TextCaption semiBold sx={textDateOfFirstPayment}>
                {t('global-bill-payment::glossary.frequency')}
              </TextCaption>
              <View sx={viewIcon}>
                <Icon name="info" color="#000000" width={15} height={15} />
              </View>
            </View>
            <View sx={dropdownFrequencyWrapper}>
              <Dropdown
                sx={{ ...dropdown, ...{ borderColor: isErrorInterval ? '$danger' : '$borderButton' } }}
                onValueChange={setInterval}
                options={[
                  {
                    label: t('global-bill-payment::glossary.daily'),
                    value: IntervalBillPayment.DAILY
                  },
                  {
                    label: t('global-bill-payment::glossary.weekly'),
                    value: IntervalBillPayment.WEEKLY
                  },
                  {
                    label: t('global-bill-payment::glossary.monthly'),
                    value: IntervalBillPayment.MONTHLY
                  }
                ]}
                value={interval}
                placeholder={t('global-bill-payment::glossary.select-frequency')}
              />
            </View>
            {isErrorInterval && (
              <TextCaption danger sx={textError}>
                {billPaymentState.interval?.message}
              </TextCaption>
            )}

            <TextCaption semiBold sx={textScheduleDate}>
              {t('global-bill-payment::glossary.payment-end-date')}
            </TextCaption>
            <View sx={{ zIndex: 500 }}>
              <DatePicker
                sx={{ borderColor: isErrorPaymentEndDate ? '$danger' : '$borderButton' }}
                dateProps={paymentEndDate}
                onValueChange={setPaymentEndDate}
                options={{
                  minDate: dateOfFirstPayment ? dateOfFirstPayment : moment()
                }}
              />
            </View>
            {isErrorPaymentEndDate && billPaymentState.paymentEndDate && (
              <TextCaption danger sx={textError}>
                {billPaymentState.paymentEndDate.message}
              </TextCaption>
            )}
            <View sx={{ ...styles.viewRow, ...{ mt: '$5' } }}>
              <TextSmall sx={styles.textNumberSchedule}>1.</TextSmall>
              <View sx={{ ...styles.viewColumn, ...{ flex: 1 } }}>
                <TextSmall sx={{ ...styles.textNote, ...styles.textNoteMarginSchedule }}>
                  {t('global-bill-payment::instruction.text--notice-your-recurring')}
                </TextSmall>
              </View>
            </View>
            <View sx={styles.viewRow}>
              <TextSmall sx={styles.textNumberSchedule}>2.</TextSmall>
              <View sx={{ ...styles.viewColumn, ...{ flex: 1 } }}>
                <TextSmall sx={{ ...styles.textNote, ...styles.textNoteMarginSchedule }}>
                  {t('global-bill-payment::instruction.text--notice-please-noted-that')}
                </TextSmall>
              </View>
            </View>
            <View sx={styles.viewRow}>
              <TextSmall sx={styles.textNumberSchedule}>3.</TextSmall>
              <View sx={{ ...styles.viewColumn, ...{ flex: 1 } }}>
                <TextSmall sx={{ ...styles.textNote, ...styles.textNoteMarginSchedule }}>
                  {t('global-bill-payment::instruction.text--notice-should-the-instruction-rejected')}
                </TextSmall>
              </View>
            </View>
            <View sx={{ flexDirection: 'row' }}>
              <TextSmall sx={styles.textNumberSchedule}>4.</TextSmall>
              <View sx={{ ...styles.viewColumn, ...{ flex: 1 } }}>
                <TextSmall sx={{ ...styles.textNote, ...styles.textNoteMarginSchedule }}>
                  {t('global-bill-payment::instruction.text--notice-if-the-payment')}
                </TextSmall>
              </View>
            </View>
          </>
        )}
      </View>
    </View>
  );
};

const styles: Record<string, SxProp> = {
  container: {
    flexDirection: 'row',
    width: '100%'
  },
  leftContainer: {
    flex: 29,
    py: '$1',
    pl: '$8',
    flexDirection: 'column'
  },
  rightContainer: {
    flex: 52
  },
  number: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '$6',
    height: '$6',
    borderRadius: 50,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '$secondary'
  },
  textNumber: {
    color: 'white'
  },
  textSchedule: {
    mt: 3,
    mb: 6
  },
  textScheduleDate: {
    mt: '$5',
    mb: '$1'
  },
  textDateOfFirstPayment: {
    mb: '$1',
    mr: '$1'
  },
  textNote: {
    color: '$textSecondary'
  },
  dropdownFrequencyWrapper: {
    flex: 1,
    height: '$12',
    zIndex: 8888
  },
  dropdown: {
    width: '100%',
    height: '$12'
  },
  viewTitleIconHorizontal: {
    flexDirection: 'row',
    alignItems: 'center',
    mt: '$5'
  },
  viewIcon: {
    mb: 3
  },
  textNoteMargin: {
    mt: '$5'
  },
  textError: {
    mt: '$1'
  },
  viewRemarksMargin: {
    mt: '$10'
  },
  viewTabWrapper: {
    alignItems: 'flex-start'
  },
  textDescription: {
    mt: '$2'
  },
  viewRow: {
    flexDirection: 'row'
  },
  viewColumn: {
    flexDirection: 'column'
  },
  textNumberSchedule: {
    color: '$textSecondary',
    width: '$3',
    textAlign: 'right'
  },
  textNoteMarginSchedule: {
    ml: '$1'
  }
};
